import React, { useEffect, useMemo, useState } from "react";
import { X, Printer, Download, Loader2, QrCode as QrIcon } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * A5 printable welcome poster preview.
 *
 * Renders at real A5 physical size (148mm × 210mm) so browser print
 * places it 1:1 on paper. In the modal, we scale the poster down
 * responsively so it fits any viewport — but the physical dimensions
 * on the .a5-poster element itself never change. On print, @media print
 * CSS (see index.css) hides everything except this root, so hitting
 * Print produces a clean single A5 sheet.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - qrUrl: string     the public URL encoded into the QR code
 *  - kind: "store" | "review" | "website"     selects QR artwork and copy
 */
export default function PosterModal({ open, onClose, qrUrl, kind = "store" }) {
  const [scale, setScale] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const isReviewPoster = kind === "review";
  const isWebsiteReviewPoster = kind === "website";
  const isReviewExperiencePoster = isReviewPoster || isWebsiteReviewPoster;

  // Responsive preview scaling. A5 portrait is 148mm × 210mm.
  // At ~3.78 px/mm at 96dpi CSS, that's ~559 × 794 CSS pixels. The modal
  // gives us a viewport up to ~90vh tall / ~500px wide, so we compute a
  // scale factor that keeps the poster fully visible on every screen.
  useEffect(() => {
    if (!open) return;
    const compute = () => {
      const posterWpx = 148 * 3.7795275591; // 1mm ≈ 3.78 CSS px
      const posterHpx = 210 * 3.7795275591;
      const availW = Math.min(window.innerWidth * 0.9, 620);
      // Reserve some vertical room for the modal chrome + action bar
      const availH = window.innerHeight * 0.72;
      const s = Math.min(availW / posterWpx, availH / posterHpx, 1);
      setScale(s);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [open]);

  const handlePrint = () => {
    // Trigger the browser's native print dialog. @media print in
    // index.css hides everything except .print-poster-root, so the
    // resulting sheet is the poster alone at A5.
    window.print();
  };

  const handleDownloadPng = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const suffix = isReviewPoster
        ? "?kind=review"
        : isWebsiteReviewPoster
          ? "?kind=website_review"
          : "";
      const res = await fetch(`${API}/admin/poster.png${suffix}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to generate poster");
      const blob = await res.blob();
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = isReviewPoster
        ? "tanish-textiles-google-review-a5-poster.png"
        : isWebsiteReviewPoster
          ? "tanish-textiles-website-ai-review-a5-poster.png"
          : "tanish-textiles-a5-poster.png";
      a.click();
      window.URL.revokeObjectURL(href);
      const message = isReviewPoster
        ? "Google Review poster downloaded"
        : isWebsiteReviewPoster
          ? "Website AI Review poster downloaded"
          : "A5 poster PNG downloaded";
      toast.success(message);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const qrSvgUrl = useMemo(() => {
    if (isReviewPoster) {
      return `${API}/admin/review-qr?format=svg&size=10`;
    }
    if (isWebsiteReviewPoster) {
      return `${API}/admin/website-review-qr?format=svg&size=10`;
    }
    return `${API}/qr?format=svg&size=10`;
  }, [isReviewPoster, isWebsiteReviewPoster]);

  if (!open) return null;

  return (
    <div
      className="print-poster-root fixed inset-0 z-50 flex flex-col bg-black/50 backdrop-blur-sm"
      data-testid="poster-modal"
    >
      {/* Screen-only header — hidden while printing */}
      <div className="no-print flex items-center justify-between px-4 sm:px-6 py-3 bg-ivory/95 border-b border-line">
        <div className="flex items-center gap-2 min-w-0">
          <QrIcon className="text-maroon flex-shrink-0" size={16} strokeWidth={1.6} />
          <p className="overline text-[10px] truncate">
            {isReviewPoster
              ? "A5 Google Review Poster"
              : isWebsiteReviewPoster
                ? "A5 Website AI Review Poster"
                : "A5 Welcome Poster"}
          </p>
        </div>
        <button
          data-testid="poster-modal-close"
          onClick={onClose}
          className="h-9 w-9 rounded-full hover:bg-ivory text-muted2 hover:text-maroon flex items-center justify-center transition-colors"
          aria-label="Close"
        >
          <X size={18} strokeWidth={1.6} />
        </button>
      </div>

      {/* Poster preview */}
      <div className="flex-1 overflow-auto flex items-start sm:items-center justify-center p-4 sm:p-6">
        <div
          style={{
            width: `${148 * 3.7795275591 * scale}px`,
            height: `${210 * 3.7795275591 * scale}px`,
            position: "relative",
            flexShrink: 0,
          }}
          className="shadow-2xl"
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            <A5Poster
              isReviewPoster={isReviewPoster}
              isWebsiteReviewPoster={isWebsiteReviewPoster}
              qrSvgUrl={qrSvgUrl}
              qrTargetUrl={
                qrUrl || (isReviewExperiencePoster ? "" : "https://tanishtextiles.co.in")
              }
            />
          </div>
        </div>
      </div>

      {/* Screen-only action bar */}
      <div className="no-print flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-4 sm:px-6 py-4 bg-ivory/95 border-t border-line">
        <p className="text-[11px] text-muted2 leading-relaxed">
          Prints at real A5 size (148 × 210 mm). Use “Print” for PDF via the browser dialog,
          or “Download PNG” for a 300 DPI file.
        </p>
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            data-testid="poster-download-png"
            onClick={handleDownloadPng}
            disabled={downloading}
            className="btn-outline-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {downloading ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} strokeWidth={1.7} />}
            Download PNG
          </button>
          <button
            data-testid="poster-print"
            onClick={handlePrint}
            className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5"
          >
            <Printer size={13} strokeWidth={1.7} /> Print / Save as PDF
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The A5 poster itself. All sizes are in real physical units (mm) so the
 * browser prints it 1:1 on paper. Kept in this file — it's not reused
 * elsewhere and duplicating the layout in a separate component would
 * only add indirection.
 */
function A5Poster({ isReviewPoster, isWebsiteReviewPoster, qrSvgUrl, qrTargetUrl }) {
  const headlineStyle = isReviewPoster
    ? { fontSize: "9.5mm", whiteSpace: "nowrap" }
    : isWebsiteReviewPoster
      ? { fontSize: "9mm" }
    : { fontSize: "11mm" };
  const subheadlineStyle = isWebsiteReviewPoster
    ? { fontSize: "4.2mm", lineHeight: 1.2, maxWidth: "112mm" }
    : headlineStyle;
  const qrSize = isReviewPoster ? "68mm" : isWebsiteReviewPoster ? "66mm" : "70mm";

  return (
    <div
      className="a5-poster"
      data-testid="a5-poster"
      style={{
        fontFamily: "'Manrope', sans-serif",
        color: "#251E1A",
      }}
    >
      {/* Elegant border frame */}
      <div
        style={{
          position: "absolute",
          inset: "6mm",
          border: "1px solid #D2C3AF",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          paddingTop: "6mm",
        }}
      >
        {/* Wordmark */}
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            color: "#6A1F2B",
            fontSize: "10mm",
            letterSpacing: "0.22em",
            margin: 0,
            lineHeight: 1.1,
            whiteSpace: "nowrap",
          }}
        >
          TANISH TEXTILES
        </h1>
        <p
          style={{
            margin: 0,
            marginTop: "4mm",
            color: "#7A6D5D",
            fontSize: "3mm",
            letterSpacing: "0.32em",
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          Bridal &nbsp;·&nbsp; Couture &nbsp;·&nbsp; Since Generations
        </p>
        <div
          style={{
            width: "14mm",
            height: "0.5mm",
            background: "#6A1F2B",
            margin: "6mm auto 0",
          }}
        />

        {/* Tagline */}
        <div style={{ marginTop: "8mm" }}>
          <p
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 300,
              ...subheadlineStyle,
              lineHeight: subheadlineStyle.lineHeight || 1.15,
              color: "#251E1A",
              margin: 0,
            }}
          >
            {isWebsiteReviewPoster
              ? "Share Your Experience"
              : isReviewPoster
                ? "Loved Your Experience?"
                : "Welcome to the"}
          </p>
          <p
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 300,
              ...headlineStyle,
              lineHeight: 1.15,
              color: "#251E1A",
              margin: 0,
              marginTop: "1mm",
            }}
          >
            {isWebsiteReviewPoster
              ? "Create your review before sharing it on Google."
              : isReviewPoster
                ? "Scan to leave us a Google Review."
                : "Tanish Textiles Family"}
          </p>
        </div>

        {/* QR panel */}
        <div
          style={{
            marginTop: "8mm",
            padding: "3mm",
            border: "1px solid #DBD2C5",
            background: "#FDFAF5",
            borderRadius: "2mm",
          }}
        >
          <img
            src={qrSvgUrl}
            alt={`QR code linking to ${qrTargetUrl}`}
            style={{
              width: qrSize,
              height: qrSize,
              display: "block",
            }}
          />
        </div>

        {/* Scan CTA */}
        <p
          style={{
            marginTop: "5mm",
            color: "#6A1F2B",
            fontWeight: 700,
            fontSize: "4.6mm",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}
        >
          {isWebsiteReviewPoster
            ? "Scan to start your review"
            : isReviewPoster
              ? "Scan to leave us a Google Review"
              : "Scan to confirm your delivery details"}
        </p>
        <p
          style={{
            marginBottom: 0,
            marginTop: "2mm",
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            color: "#7A6D5D",
            fontSize: "4.2mm",
          }}
        >
          {isWebsiteReviewPoster
            ? "Your draft is ready before you share it on Google."
            : isReviewPoster
              ? "Thank you for sharing your experience."
              : "It only takes a minute."}
        </p>

        {/* Spacer pushes footer to bottom */}
        <div style={{ flex: 1 }} />

        {/* Footer */}
        <div
          style={{
            width: "70%",
            borderTop: "1px solid #DBD2C5",
            paddingTop: "3mm",
          }}
        >
          <p
            style={{
              margin: 0,
              fontWeight: 700,
              fontSize: "3.4mm",
              letterSpacing: "0.22em",
              color: "#251E1A",
            }}
          >
            TANISH TEXTILES &nbsp;·&nbsp; MUMBAI
          </p>
          <p
            style={{
              marginBottom: 0,
              marginTop: "3mm",
              fontSize: "3mm",
              color: "#7A6D5D",
              letterSpacing: "0.02em",
            }}
          >
            WhatsApp&nbsp; +91 84339 10466 &nbsp;·&nbsp; Instagram&nbsp; @tanish_textiles
          </p>
          <p
            style={{
              marginBottom: 0,
              marginTop: "2mm",
              fontSize: "2.6mm",
              color: "#A69985",
              letterSpacing: "0.02em",
            }}
          >
            {qrTargetUrl}
          </p>
        </div>
      </div>
    </div>
  );
}
